import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Nothing here — `.w-empty` from the web design handoff.
 *
 * An empty state that only says "no results" is a dead end. Every one of these
 * carries a way forward, because the reader arrived expecting something and the
 * page's job is to say what to do instead.
 */
export function EmptyState({
  icon = '⌕',
  title,
  body,
  action,
}: {
  icon?: string;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="w-empty">
      <div className="w-empty-i" aria-hidden>
        {icon}
      </div>
      <div>
        <h3 className="w-h3" style={{ fontSize: 17 }}>
          {title}
        </h3>
        <p className="w-body muted" style={{ marginTop: 6, maxWidth: 400 }}>
          {body}
        </p>
      </div>
      {action}
    </div>
  );
}

/**
 * Signed out, this page has nothing to show.
 *
 * 🔴 NOT AN EMPTY LIST. Row-level security returns nothing to a guest, so a
 * watchlist or a dashboard rendered for one would be either blank — reading as
 * "you have nothing", which is a claim we cannot make — or, far worse, filled
 * with somebody else's money. The gate says what signing in unlocks and stops.
 *
 * It carries the current location so sign-in can return the visitor to the page
 * they were actually trying to reach.
 */
export function GuestGate({
  title,
  body,
  secondaryLabel = 'Register to bid',
  secondaryTo = '/register',
}: {
  title: string;
  body: string;
  /** Overridden on the register page itself, where "Register to bid" is a loop. */
  secondaryLabel?: string;
  secondaryTo?: string;
}) {
  const location = useLocation();
  return (
    <div className="w-empty" style={{ borderStyle: 'solid', background: 'var(--surface-elev)' }}>
      <div className="w-empty-i" aria-hidden>
        ☺
      </div>
      <div>
        <h3 className="w-h3" style={{ fontSize: 18 }}>
          {title}
        </h3>
        <p className="w-body muted" style={{ marginTop: 6, maxWidth: 420 }}>
          {body}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Link to="/signin" state={{ from: location }} className="w-btn w-btn-cta">
          Sign in
        </Link>
        <Link to={secondaryTo} className="w-btn w-btn-out">
          {secondaryLabel}
        </Link>
      </div>
    </div>
  );
}
